import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ButtonComponent } from '@shared/components/button/button.component'
import { Subscription } from 'rxjs'
import { IncommingChipsUpdateDto, PlayerBetDto, PlayerDto, SeatDto, TableInfoDto } from '@core/dtos'
import { SmSpinnerComponent } from '@shared/components/sm-spinner/sm-spinner.component'
import { AccountService, WebSocketService } from '@core/services'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faArrowRightToBracket, faCoins, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { PlayerService } from '../../../../@shared/services/player.service'
import { BetTypeEnum, EasyPickEnum } from '@shared/enums'
import { EasyPickListConst } from '@shared/constants'
import { ModeEnum } from '@core/enums'
import { BettedChipsComponent } from './betted-chips/betted-chips.component'
import { IncommingChipsLabelComponent } from './incomming-chips-label/incomming-chips-label.component'
import {
  ArrowRightSvgComponent,
  InfoSvgComponent,
  ModalComponent,
  ProfileComponent,
} from '@shared/components'
import { ChipsRequestComponent } from '../chips-request/chips-request.component'
import { ModalService } from '@shared/services'
import { Router } from '@angular/router'
import { ClassicService } from '@rush-shared/services'

@Component({
  selector: 'app-betting-section',
  standalone: true,
  imports: [
    BettedChipsComponent,
    CommonModule,
    ButtonComponent,
    SmSpinnerComponent,
    FormsModule,
    FontAwesomeModule,
    IncommingChipsLabelComponent,
    ModalComponent,
    ChipsRequestComponent,
    ProfileComponent,
    InfoSvgComponent,
    ArrowRightSvgComponent,
  ],
  templateUrl: './betting-section.component.html',
  styleUrl: './betting-section.component.sass',
  providers: [WebSocketService],
})
export class BettingSectionComponent implements OnInit, OnDestroy {
  private readonly _subscription = new Subscription()

  readonly playerId = this._accountService.currentUserId

  @Input() dealerId: string
  @Input() countdown: number

  selectedBet?: BetTypeEnum
  betType = BetTypeEnum
  showSpinner = false
  faArrowRightToBracket = faArrowRightToBracket
  faArrowRight = faArrowRight
  faCoins = faCoins
  easyPicks = EasyPickListConst
  selectedEasyPick?: EasyPickEnum
  modeType = ModeEnum
  showChipsRequestModal = false

  get allowedChipsToBuyin(): number {
    return this.minBet + this._playerService.foldCost
  }
  get incommingChips(): IncommingChipsUpdateDto | undefined {
    return this._playerService.incommingChips
  }
  get isWinner(): boolean {
    return this._playerService.playerBetResult?.isWinner || false
  }
  get showResult(): boolean {
    return this._playerService.showResult && (this.chipsResult > 0 || this.betAmount > 0)
  }
  get chipsResult(): number {
    return Math.abs(this._playerService.playerBetResult?.earnedChips || 0)
  }
  get betAmount(): number {
    return Number(this._playerService.playerBet?.chips || 0)
  }
  set betAmount(amount: number) {
    if (this._playerService.playerBet) {
      this._playerService.playerBet.chips = Math.floor(amount) || 0
    }
  }

  get playerBet(): PlayerBetDto {
    return (
      this._playerService.playerBet || {
        playerId: '',
        seatNo: 0,
        chips: 0,
        earnedChips: 0,
      }
    )
  }
  get canLeave(): boolean {
    return this.currentSeat === undefined || this.currentSeat === null
  }
  get disableBetting(): boolean {
    return (
      this.maxBet < this.minBet ||
      this.chips < this.minBet ||
      this.betAmount <= 0 ||
      this.isBetLock ||
      this.betAmount > this.maxBet ||
      this.betAmount < this.minBet
    )
  }
  get disableBuyIn(): boolean {
    return (
      this.chips < this.allowedChipsToBuyin ||
      !this._playerService.allowBuyIn ||
      this._playerService.invalidDealer
    )
  }
  get isBetLock(): boolean {
    return (
      this._playerService.isBetLock === true ||
      this._playerService.playerBet?.isLocked === true ||
      this.mode !== ModeEnum.PlayerBets
    )
  }
  get isFold(): boolean {
    return this.selectedBet === BetTypeEnum.FOLD
  }
  get isPair(): boolean {
    return this._playerService.isPair
  }
  get chips(): number {
    return this._playerService.activePlayer?.chips || 0
  }
  get potBonusAmount(): number {
    return this._playerService.potBonus || 0
  }
  get mode(): ModeEnum {
    return this._classicService?.mode$.value || 0
  }
  get activePlayer(): PlayerDto {
    return this._playerService.activePlayer
  }
  get currentSeat(): SeatDto | undefined {
    return this._playerService.currentSeat
  }
  get currentSeatNo(): number {
    return this.currentSeat?.seatNo || 0
  }
  get showBuyIn(): boolean {
    return (
      !this.currentSeat &&
      (this.mode === this.modeType.BuyInOpen || this.mode === this.modeType.Setup)
    )
  }
  get currentSeatNoFromDealer(): number {
    return this._playerService.currentSeatNoFromDealer
  }
  get isPlayerTurn(): boolean {
    return (
      this.currentSeatNo === this._playerService.currentSeatNoFromDealer &&
      this.mode !== this.modeType.BuyInOpen
    )
  }
  get maxBet(): number {
    return this._playerService.maxBet < this.chips ? this._playerService.maxBet : this.chips
  }
  get minBet(): number {
    return this._playerService.minBet
  }
  get allowBuyin(): boolean {
    return this._playerService.allowBuyIn
  }
  get buyInLabel(): string {
    if (this._playerService.allowBuyIn) {
      // return `BUY IN ${this._playerService.buyInCost} CHIPS`
    }

    return 'Buy In Closed'
  }

  get tableInfo(): TableInfoDto | null {
    return this._playerService.tableInfo
  }

  constructor(
    public _playerService: PlayerService,
    private router: Router,
    private _accountService: AccountService,
    private _webSocketService: WebSocketService,
    private _modalService: ModalService,
    private _classicService: ClassicService
  ) {}

  ngOnInit(): void {
    this._subscription.add(
      this._playerService.currentSeat$.subscribe((i) => {
        this.showSpinner = false
      }),
    )
    this._webSocketService.listenToBetResult(this.playerId, () => this.reset())
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe()
    this._playerService.potBonus = 0
    this.reset()
  }

  onClickEasyPick(easyPick: EasyPickEnum) {
    if (!this._playerService.playerBet) {
      this._playerService.initializeBet()
    }

    this.betAmount = this.getEasyPickAmount(easyPick)
    this.selectedEasyPick = easyPick
  }

  getEasyPickAmount(easyPick: EasyPickEnum): number {
    return Math.floor(this.maxBet * easyPick)
  }

  onClickBuyIn(): void {
    debugger
    if (this.chips < this.minBet) {
      return
    }

    this.showSpinner = true
    this._playerService.joinTable(this._playerService.activePlayer.id)
  }

  onClickLeaveRoom(): void {
    if (!this.canLeave) {
      return
    }
    this._playerService.leave().subscribe(() => {
      this.router.navigate([`menu/${this._playerService.activePlayer.id}`])
    })
  }

  onClickSetBet(bet: BetTypeEnum): void {
    this.selectedBet = bet
    this._playerService.disableBetting()

    this.onClickLockBet()
  }

  reset(): void {
    this.selectedBet = undefined
    this.betAmount = 0
    this.selectedEasyPick = undefined
    this._playerService.isBetLock = false
    this._playerService.clearIncommingChipsUpdate()
  }

  onClickLockBet() {
    if (this.chips < this.betAmount || this.betAmount <= 0) {
      return
    }

    this._playerService.isBetLock = true
    this.showSpinner = true

    const bet = this.selectedBet === undefined ? BetTypeEnum.FOLD : this.selectedBet

    this._playerService.lockBet(this.betAmount, bet).subscribe((i) => {
      this.showSpinner = false
    })
  }

  onClickFoldBet() {
    this.betAmount = 0
    this.selectedBet = BetTypeEnum.FOLD
    this._playerService.disableBetting()

    this._playerService.isBetLock = true
    this.showSpinner = true

    this._playerService.lockBet(this.betAmount, this.selectedBet).subscribe((i) => {
      this._playerService.activePlayer.chips = i.user.chips

      this.showSpinner = false
    })
  }

  disableEasyPick(value: EasyPickEnum) {
    const betAmount = this.maxBet * value

    return this.minBet > betAmount
  }

  hideIncommingChipsLabel() {
    if (this._playerService.incommingChips) this._playerService.incommingChips.show = false
  }

  onClickShowChipsRequestModal() {
    this.showChipsRequestModal = true
  }

  openInfoModal() {
    this._modalService.openTableInfoModal(this.tableInfo)
  }
}
